import {
  collection,
  doc,
  setDoc,
  getDocs,
  getDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  Timestamp,
  serverTimestamp,
  writeBatch,
  query,
  where,
  documentId,
} from 'firebase/firestore';
import { Platform } from 'react-native';
import { emailBookingDeterministicDocId } from '@/utils/emailBookingDeterministicDocId';
import { smartUploadBookingDocId } from '@/utils/smartUploadBookingDocId';
import { fromLocalDateKey } from '@/utils/dateKeys';
import { db } from '@/lib/firebase';
import { networkMonitor } from '@/services/networkMonitor';
import { syncQueue } from '@/services/syncQueue';
import type { BookingType, ExtractedBooking } from './inboxSync';
import type { InboxProviderId } from './inboxSync';
import type { AttachmentSource, ItineraryAttachment } from './itineraryAttachments';

const NATIVE_BOOKINGS_POLL_MS = 30000;

export type StatusUnavailableReason = 'no_credentials' | 'no_data' | 'api_error' | 'future';

export type FlightStatus = {
  status: 'scheduled' | 'on-time' | 'delayed' | 'cancelled' | 'unknown';
  scheduledDeparture?: Date;
  actualDeparture?: Date;
  scheduledArrival?: Date;
  actualArrival?: Date;
  delayMinutes?: number;
  gate?: string;
  terminal?: string;
  provider?: 'airlabs' | 'aerodatabox' | 'aviationstack' | 'opensky';
  isStale?: boolean;
  nextRefreshAt?: Date;
  lastUpdated?: Date;
  /** Set when status is unknown so the UI can show why (e.g. future, no_data, no_credentials). */
  statusUnavailableReason?: StatusUnavailableReason;
};

export type FlightNotificationPreferences = {
  delays: boolean;
  gateChanges: boolean;
  checkInReminders: boolean;
};

type BookingFlightMetadata = {
  flightStatus?: FlightStatus;
  checkInLink?: string;
  checkInAvailable?: boolean;
  gate?: string;
  terminal?: string;
  flightNumber?: string;
  airlineCode?: string;
  notificationPreferences?: FlightNotificationPreferences;
  lastStatusCheck?: Date;
};

// Import split types from expenses
export type BookingSplitType = 'none' | 'equal' | 'custom';

export type BookingSplit = {
  splitType: BookingSplitType;
  splitAmong: string[]; // Array of userIds
  paidBy: string; // userId of person who paid
  splitAmounts?: Record<string, number>; // For custom splits: { userId: amount }
};

export type Booking = ExtractedBooking & BookingFlightMetadata & {
  id: string;
  tripId: string;
  userId: string;
  createdAt: Date;
  syncedAt: Date;
  status?: 'pending' | 'confirmed' | 'cancelled' | 'completed';
  emailProvider?: 'gmail' | 'outlook' | 'icloud'; // Which email provider this booking came from
  attachments?: ItineraryAttachment[];
  split?: BookingSplit; // Optional expense split for collaborative trips
};

type FirestoreFlightStatus = {
  status: FlightStatus['status'];
  scheduledDeparture?: Timestamp | string;
  actualDeparture?: Timestamp | string;
  scheduledArrival?: Timestamp | string;
  actualArrival?: Timestamp | string;
  delayMinutes?: number;
  gate?: string;
  terminal?: string;
  provider?: FlightStatus['provider'];
  isStale?: boolean;
  nextRefreshAt?: Timestamp | string;
  lastUpdated?: Timestamp | string;
  statusUnavailableReason?: FlightStatus['statusUnavailableReason'];
};

type FirestoreFlightNotificationPreferences = {
  delays?: boolean;
  gateChanges?: boolean;
  checkInReminders?: boolean;
};

type FirestoreBooking = {
  type: string;
  provider: string;
  confirmationNumber?: string;
  checkInDate?: Timestamp | string;
  checkOutDate?: Timestamp | string;
  departureDate?: Timestamp | string;
  arrivalDate?: Timestamp | string;
  startTime?: string;
  endTime?: string;
  location?: string;
  address?: string; // For hotel bookings - full address
  origin?: string;
  destination?: string;
  segments?: Array<{
    origin?: string;
    destination?: string;
    departureDate?: Timestamp | string;
    arrivalDate?: Timestamp | string;
    departureTime?: string;
    arrivalTime?: string;
    flightNumber?: string;
    airlineCode?: string;
  }>;
  amount?: number;
  currency?: string;
  rawEmailSubject?: string;
  rawEmailDate?: Timestamp | string;
  status?: 'pending' | 'confirmed' | 'cancelled' | 'completed';
  tripId: string;
  userId: string;
  createdAt: Timestamp | Date;
  syncedAt: Timestamp | Date;
  source?: string;
  emailProvider?: 'gmail' | 'outlook' | 'icloud'; // Which email provider this booking came from
  flightStatus?: FirestoreFlightStatus;
  checkInLink?: string;
  checkInAvailable?: boolean;
  gate?: string;
  terminal?: string;
  flightNumber?: string;
  airlineCode?: string;
  notificationPreferences?: FirestoreFlightNotificationPreferences;
  lastStatusCheck?: Timestamp | string;
  attachments?: Array<Partial<ItineraryAttachment>>;
  split?: BookingSplit; // Optional expense split
};

const convertDate = (date: Timestamp | string | Date | undefined): Date | undefined => {
  if (!date) return undefined;
  if (date instanceof Timestamp) return date.toDate();
  if (date instanceof Date) return date;
  if (typeof date === 'string') {
    const parsed = new Date(date);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  }
  return undefined;
};

const convertFlightStatus = (status?: FirestoreFlightStatus): FlightStatus | undefined => {
  if (!status) {
    return undefined;
  }

  return {
    status: status.status,
    scheduledDeparture: convertDate(status.scheduledDeparture),
    actualDeparture: convertDate(status.actualDeparture),
    scheduledArrival: convertDate(status.scheduledArrival),
    actualArrival: convertDate(status.actualArrival),
    delayMinutes: status.delayMinutes,
    gate: status.gate,
    terminal: status.terminal,
    provider: status.provider,
    isStale: status.isStale,
    nextRefreshAt: convertDate(status.nextRefreshAt),
    lastUpdated: convertDate(status.lastUpdated),
    statusUnavailableReason: status.statusUnavailableReason,
  };
};

const convertNotificationPreferences = (
  prefs?: FirestoreFlightNotificationPreferences
): FlightNotificationPreferences | undefined => {
  if (!prefs) {
    return undefined;
  }

  return {
    delays: prefs.delays ?? true,
    gateChanges: prefs.gateChanges ?? true,
    checkInReminders: prefs.checkInReminders ?? true,
  };
};

const coerceAttachmentSource = (value: unknown): AttachmentSource | undefined => {
  if (value === 'email' || value === 'upload' || value === 'manual' || value === 'sync') {
    return value;
  }
  return undefined;
};

const convertBookingAttachments = (raw?: FirestoreBooking['attachments']): Booking['attachments'] => {
  if (!raw?.length) return undefined;
  const out: NonNullable<Booking['attachments']> = [];
  for (let i = 0; i < raw.length; i++) {
    const a = raw[i];
    if (!a?.url || typeof a.url !== 'string') continue;
    out.push({
      id: (typeof a.id === 'string' && a.id.trim()) || `att-${i}`,
      name: (typeof a.name === 'string' && a.name.trim()) || 'Attachment',
      url: a.url.trim(),
      contentType: a.contentType == null ? undefined : a.contentType,
      size: a.size == null ? undefined : a.size,
      source: coerceAttachmentSource(a.source),
    });
  }
  return out.length ? out : undefined;
};

const convertSegments = (
  segments?: FirestoreBooking['segments']
): ExtractedBooking['segments'] | undefined => {
  if (!segments || segments.length === 0) return undefined;
  const converted = segments.map((s) => ({
    origin: s.origin,
    destination: s.destination,
    departureDate: convertDate(s.departureDate),
    arrivalDate: convertDate(s.arrivalDate),
    departureTime: s.departureTime,
    arrivalTime: s.arrivalTime,
    flightNumber: s.flightNumber,
    airlineCode: s.airlineCode,
  }));
  return converted.length ? (converted as any) : undefined;
};

// Convert Firestore booking to app Booking type
const convertFirestoreBooking = (id: string, data: FirestoreBooking): Booking => {
  return {
    id,
    type: data.type as ExtractedBooking['type'],
    provider: data.provider,
    confirmationNumber: data.confirmationNumber,
    checkInDate: convertDate(data.checkInDate),
    checkOutDate: convertDate(data.checkOutDate),
    departureDate: convertDate(data.departureDate),
    arrivalDate: convertDate(data.arrivalDate),
    startTime: data.startTime,
    endTime: data.endTime,
    location: data.location,
    address: data.address,
    origin: data.origin,
    destination: data.destination,
    segments: convertSegments(data.segments),
    amount: data.amount,
    currency: data.currency,
    rawEmailSubject: data.rawEmailSubject,
    rawEmailDate: convertDate(data.rawEmailDate),
    status: data.status || 'confirmed', // Default to confirmed for existing bookings
    tripId: data.tripId,
    userId: data.userId,
    createdAt: convertDate(data.createdAt) || new Date(),
    syncedAt: convertDate(data.syncedAt) || new Date(),
    source: (data as any).source,
    emailProvider: data.emailProvider, // Which email provider this booking came from
    flightStatus: convertFlightStatus(data.flightStatus),
    checkInLink: data.checkInLink,
    checkInAvailable: data.checkInAvailable,
    gate: data.gate,
    terminal: data.terminal,
    flightNumber: data.flightNumber,
    airlineCode: data.airlineCode,
    notificationPreferences: convertNotificationPreferences(data.notificationPreferences),
    lastStatusCheck: convertDate(data.lastStatusCheck),
    attachments: convertBookingAttachments(data.attachments),
    split: data.split, // Pass through split data
  };
};

/**
 * Fetch all bookings for a trip
 */
export const fetchTripBookings = async (tripId: string): Promise<Booking[]> => {
  try {
    const bookingsRef = collection(db, 'trips', tripId, 'bookings');
    const snapshot = await getDocs(bookingsRef);
    
    return snapshot.docs.map(doc => 
      convertFirestoreBooking(doc.id, doc.data() as FirestoreBooking)
    );
  } catch (error) {
    console.error('Error fetching bookings:', error);
    return [];
  }
};

/**
 * Subscribe to real-time updates for trip bookings
 */
export const subscribeToTripBookings = (
  tripId: string,
  callback: (bookings: Booking[]) => void
): (() => void) => {
  if (Platform.OS !== 'web') {
    let isCleanedUp = false;
    const poll = async () => {
      try {
        const bookings = await fetchTripBookings(tripId);
        if (!isCleanedUp) {
          callback(bookings);
        }
      } catch (error) {
        console.error('[Bookings] Native polling fetch failed:', error);
        if (!isCleanedUp) {
          callback([]);
        }
      }
    };
    void poll();
    const interval = setInterval(() => {
      void poll();
    }, NATIVE_BOOKINGS_POLL_MS);
    return () => {
      isCleanedUp = true;
      clearInterval(interval);
    };
  }

  const bookingsRef = collection(db, 'trips', tripId, 'bookings');
  
  let unsubscribeFn: (() => void) | null = null;
  let isCleanedUp = false;
  
  try {
    unsubscribeFn = onSnapshot(bookingsRef, (snapshot) => {
      // Don't process updates if already cleaned up
      if (isCleanedUp) {
        return;
      }
      
      // Defer heavy work to avoid blocking message handlers
      setTimeout(() => {
        if (isCleanedUp) {
          return;
        }
        try {
          const bookings = snapshot.docs.map(doc =>
            convertFirestoreBooking(doc.id, doc.data() as FirestoreBooking)
          );
          callback(bookings);
        } catch (error) {
          console.error('[Bookings] Error processing snapshot:', error);
          if (!isCleanedUp) {
            try {
              callback([]);
            } catch (callbackError) {
              console.error('[Bookings] Error in callback:', callbackError);
            }
          }
        }
      }, 0);
    }, (error) => {
      // Don't process errors if already cleaned up
      if (isCleanedUp) {
        return;
      }
      
      console.error('Error in bookings subscription:', error);
      
      // For permission errors, immediately mark as cleaned up and don't retry
      // This prevents Firestore internal state corruption
      const errCode = error && typeof error === 'object' && 'code' in error ? String((error as { code?: string }).code) : '';
      if (errCode === 'permission-denied' || errCode === 'missing-or-insufficient-permissions') {
        console.warn('[Bookings] Permission denied, cleaning up subscription to prevent state corruption');
        isCleanedUp = true;
        if (unsubscribeFn) {
          try {
            unsubscribeFn();
          } catch (unsubError) {
            // Ignore cleanup errors
          }
          unsubscribeFn = null;
        }
        return;
      }
      
      // For other errors, call callback with empty array
      try {
        callback([]);
      } catch (callbackError) {
        console.error('[Bookings] Error in callback:', callbackError);
      }
    });
  } catch (error) {
    console.error('[Bookings] Error setting up subscription:', error);
    // Return a no-op cleanup function if setup fails
    return () => {};
  }
  
  return () => {
    if (isCleanedUp) {
      return; // Already cleaned up, prevent double cleanup
    }
    isCleanedUp = true;
    
    if (unsubscribeFn) {
      try {
        unsubscribeFn();
      } catch (error) {
        // Silently ignore cleanup errors - they're often due to Firestore internal state
        // and logging them causes noise
      } finally {
        unsubscribeFn = null; // Clear reference after cleanup
      }
    }
  };
};

/**
 * Create a new booking manually
 */
type ManualBookingInput = Omit<ExtractedBooking, 'rawEmailDate'> &
  BookingFlightMetadata & {
    /**
     * Source system for this booking.
     * - 'manual': user-entered or app-created without an upstream system
     * - 'email': extracted from an email provider
     * - 'smart_upload': cost line from uploaded documents (paired with itinerary on client)
     * - 'amadeus': legacy in-app provider flow
     */
    source?: string;
  };

export const createBooking = async (
  tripId: string,
  userId: string,
  booking: ManualBookingInput,
  options?: { presetBookingId?: string; bypassOffline?: boolean }
): Promise<string> => {
  try {
    if (!options?.bypassOffline && networkMonitor.shouldUseOfflineDataFallback()) {
      const tempId =
        options?.presetBookingId ??
        `offline_booking_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      await syncQueue.queueAction({
        type: 'create_booking',
        payload: {
          tripId,
          userId,
          tempId,
          bookingJson: JSON.stringify(booking, (_key, v) => (v instanceof Date ? v.toISOString() : v)),
        },
        tripId,
        userId,
      });
      return tempId;
    }

    const bookingsRef = collection(db, 'trips', tripId, 'bookings');
    const newBookingRef = options?.presetBookingId
      ? doc(bookingsRef, options.presetBookingId)
      : doc(bookingsRef);

    const firestoreBooking: Record<string, any> = {
      type: booking.type,
      provider: booking.provider,
      tripId,
      userId,
      createdAt: serverTimestamp(),
      syncedAt: serverTimestamp(),
      // Manual bookings should be marked so backend sync won't delete/recreate user-authored itinerary items.
      source: (booking as any).source || 'manual',
    };

    if (booking.confirmationNumber) {
      firestoreBooking.confirmationNumber = booking.confirmationNumber;
    }
    if (booking.checkInDate) {
      firestoreBooking.checkInDate = Timestamp.fromDate(booking.checkInDate);
    }
    if (booking.checkOutDate) {
      firestoreBooking.checkOutDate = Timestamp.fromDate(booking.checkOutDate);
    }
    if (booking.departureDate) {
      firestoreBooking.departureDate = Timestamp.fromDate(booking.departureDate);
    }
    if (booking.arrivalDate) {
      firestoreBooking.arrivalDate = Timestamp.fromDate(booking.arrivalDate);
    }
    if (booking.location) {
      firestoreBooking.location = booking.location;
    }
    if (booking.address) {
      firestoreBooking.address = booking.address;
    }
    if (booking.amount !== undefined) {
      firestoreBooking.amount = booking.amount;
    }
    if (booking.currency) {
      firestoreBooking.currency = booking.currency;
    }
    if (booking.rawEmailSubject) {
      firestoreBooking.rawEmailSubject = booking.rawEmailSubject;
    }
      if ((booking as any).startTime) {
        firestoreBooking.startTime = (booking as any).startTime;
      }
      if ((booking as any).endTime) {
        firestoreBooking.endTime = (booking as any).endTime;
      }
    if (booking.status) {
      firestoreBooking.status = booking.status;
    }
    if (booking.flightNumber) {
      firestoreBooking.flightNumber = booking.flightNumber;
    }
    if (booking.airlineCode) {
      firestoreBooking.airlineCode = booking.airlineCode;
    }
    if (booking.gate) {
      firestoreBooking.gate = booking.gate;
    }
    if (booking.terminal) {
      firestoreBooking.terminal = booking.terminal;
    }
    if (booking.checkInLink) {
      firestoreBooking.checkInLink = booking.checkInLink;
    }
    if (typeof booking.checkInAvailable === 'boolean') {
      firestoreBooking.checkInAvailable = booking.checkInAvailable;
    }
    if (booking.lastStatusCheck) {
      firestoreBooking.lastStatusCheck = Timestamp.fromDate(booking.lastStatusCheck);
    }
    if (booking.notificationPreferences) {
      firestoreBooking.notificationPreferences = {
        delays: booking.notificationPreferences.delays,
        gateChanges: booking.notificationPreferences.gateChanges,
        checkInReminders: booking.notificationPreferences.checkInReminders,
      };
    }
    if (booking.flightStatus) {
      firestoreBooking.flightStatus = {
        status: booking.flightStatus.status,
        scheduledDeparture: booking.flightStatus.scheduledDeparture
          ? Timestamp.fromDate(booking.flightStatus.scheduledDeparture)
          : undefined,
        actualDeparture: booking.flightStatus.actualDeparture
          ? Timestamp.fromDate(booking.flightStatus.actualDeparture)
          : undefined,
        scheduledArrival: booking.flightStatus.scheduledArrival
          ? Timestamp.fromDate(booking.flightStatus.scheduledArrival)
          : undefined,
        actualArrival: booking.flightStatus.actualArrival
          ? Timestamp.fromDate(booking.flightStatus.actualArrival)
          : undefined,
        delayMinutes: booking.flightStatus.delayMinutes,
        gate: booking.flightStatus.gate,
        terminal: booking.flightStatus.terminal,
        provider: booking.flightStatus.provider,
        isStale: booking.flightStatus.isStale,
        nextRefreshAt: booking.flightStatus.nextRefreshAt
          ? Timestamp.fromDate(booking.flightStatus.nextRefreshAt)
          : undefined,
        lastUpdated: booking.flightStatus.lastUpdated
          ? Timestamp.fromDate(booking.flightStatus.lastUpdated)
          : undefined,
        statusUnavailableReason: booking.flightStatus.statusUnavailableReason,
      };
    }
    if (Array.isArray((booking as any).segments) && (booking as any).segments.length > 0) {
      firestoreBooking.segments = ((booking as any).segments as Array<Record<string, unknown>>).map((seg) => {
        const out: Record<string, unknown> = { ...seg };
        for (const key of ['departureDate', 'arrivalDate'] as const) {
          const v = seg[key];
          if (v instanceof Date) {
            out[key] = Timestamp.fromDate(v);
          } else if (typeof v === 'string' && v) {
            const d = new Date(v);
            if (!Number.isNaN(d.getTime())) out[key] = Timestamp.fromDate(d);
          }
        }
        return out;
      });
    }
    if (booking.attachments && booking.attachments.length > 0) {
      firestoreBooking.attachments = booking.attachments.map((att) => {
        const createdAt =
          att && typeof att === 'object' && 'createdAt' in att && att.createdAt instanceof Date
            ? Timestamp.fromDate(att.createdAt)
            : serverTimestamp();
        let expiresAt: Timestamp | null | undefined = undefined;
        if (att && typeof att === 'object' && 'expiresAt' in att && att.expiresAt != null) {
          if (att.expiresAt instanceof Date) {
            expiresAt = Timestamp.fromDate(att.expiresAt);
          } else if (typeof att.expiresAt === 'string' && att.expiresAt) {
            const d = new Date(att.expiresAt);
            expiresAt = Number.isNaN(d.getTime()) ? null : Timestamp.fromDate(d);
          }
        }
        const rest =
          att && typeof att === 'object'
            ? Object.fromEntries(
                Object.entries(att as Record<string, unknown>).filter(([key]) => key !== 'expiresAt')
              )
            : {};
        return {
          ...rest,
          createdAt,
          ...(expiresAt !== undefined ? { expiresAt } : {}),
        };
      });
    }

    await setDoc(newBookingRef, firestoreBooking);
    return newBookingRef.id;
  } catch (error) {
    console.error('Error creating booking:', error);
    throw error;
  }
};

/** One Smart Upload review row; `id` must stay stable (`${jobId}-${idx}`). */
export type SmartUploadCostBookingItem = {
  id: string;
  type?: string;
  category?: string;
  provider?: string;
  confirmationNumber?: string;
  address?: string;
  destination?: string;
  startTime?: string;
  endTime?: string;
  startDate?: string | Date | null;
  endDate?: string | Date | null;
  title?: string;
  amount?: number | null;
  currency?: string | null;
};

function parseSmartUploadItemDate(value?: string | Date | null): Date | undefined {
  if (value === undefined || value === null) return undefined;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value;
  }
  const str = String(value).trim();
  const ym = /^(\d{4}-\d{2}-\d{2})/.exec(str);
  if (ym) {
    const d = fromLocalDateKey(ym[1]);
    return Number.isNaN(d.getTime()) ? undefined : d;
  }
  const parsed = new Date(str);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}

function normalizeSmartUploadBookingType(item: SmartUploadCostBookingItem): BookingType {
  const cat = item.category;
  if (cat === 'train') return 'transportation';
  if (
    cat === 'flight' ||
    cat === 'hotel' ||
    cat === 'car' ||
    cat === 'restaurant' ||
    cat === 'activity' ||
    cat === 'transportation' ||
    cat === 'ferry' ||
    cat === 'shopping' ||
    cat === 'other'
  ) {
    return cat;
  }
  const t = String(item.type || 'other').toLowerCase();
  const map: Record<string, BookingType> = {
    flight: 'flight',
    hotel: 'hotel',
    car: 'car',
    rail: 'transportation',
    train: 'transportation',
    ferry: 'ferry',
    activity: 'activity',
    restaurant: 'restaurant',
    shopping: 'shopping',
    other: 'other',
  };
  return map[t] ?? 'other';
}

function manualFieldsForSmartUploadDates(item: SmartUploadCostBookingItem, bt: BookingType): Partial<ManualBookingInput> {
  const start = parseSmartUploadItemDate(item.startDate);
  const end = parseSmartUploadItemDate(item.endDate);
  if (!start) return {};
  if (bt === 'hotel') {
    return {
      checkInDate: start,
      checkOutDate: end ?? start,
    };
  }
  return {
    departureDate: start,
    arrivalDate: end ?? start,
  };
}

/**
 * Writes trip bookings for extracted costs and refreshes trip budget rollups (same path as inbox import).
 */
export async function recordSmartUploadCostsAsBookings(
  tripId: string,
  userId: string,
  items: SmartUploadCostBookingItem[],
): Promise<void> {
  const tripSnap = await getDoc(doc(db, 'trips', tripId));
  const baseCurrency = typeof tripSnap.data()?.baseCurrency === 'string'
    ? tripSnap.data()!.baseCurrency
    : 'USD';

  let wroteAny = false;
  for (const item of items) {
    const rawAmount = item.amount;
    if (rawAmount === undefined || rawAmount === null) continue;
    if (typeof rawAmount !== 'number' || !Number.isFinite(rawAmount)) continue;

    const curRaw = typeof item.currency === 'string' ? item.currency.trim() : '';
    const currency = curRaw || baseCurrency;
    const startOk = Boolean(parseSmartUploadItemDate(item.startDate));
    if (!startOk) continue;

    const bookingType = normalizeSmartUploadBookingType(item);
    const dateFields = manualFieldsForSmartUploadDates(item, bookingType);

    await createBooking(
      tripId,
      userId,
      {
        type: bookingType,
        provider: (item.provider && item.provider.trim()) || 'Document',
        ...dateFields,
        confirmationNumber: item.confirmationNumber,
        location: item.destination ?? item.address,
        address: item.address,
        destination: item.destination,
        amount: rawAmount,
        currency,
        status: 'confirmed',
        source: 'smart_upload',
        ...(item.startTime ? { startTime: item.startTime } : {}),
        ...(item.endTime ? { endTime: item.endTime } : {}),
      } satisfies ManualBookingInput,
      {
        presetBookingId: smartUploadBookingDocId(tripId, item.id),
        bypassOffline: false,
      },
    );
    wroteAny = true;
  }

  if (wroteAny) {
    await updateTripBudgetFromBookings(tripId);
  }
}

/**
 * Update an existing booking (e.g., to correct parsed amounts)
 */
export type BookingUpdate = {
  amount?: number;
  currency?: string;
  provider?: string;
  location?: string;
  rawEmailSubject?: string;
  confirmationNumber?: string;
  type?: 'flight' | 'hotel' | 'car' | 'restaurant' | 'activity' | 'other';
  checkInDate?: Date;
  checkOutDate?: Date;
  departureDate?: Date;
  arrivalDate?: Date;
  status?: 'pending' | 'confirmed' | 'cancelled' | 'completed';
  airlineCode?: string | null;
  flightNumber?: string | null;
  gate?: string | null;
  terminal?: string | null;
  checkInLink?: string | null;
  checkInAvailable?: boolean;
  flightStatus?: FlightStatus | null;
  lastStatusCheck?: Date | null;
  notificationPreferences?: FlightNotificationPreferences | null;
  split?: BookingSplit | null; // null to remove split
};

export const updateBooking = async (
  tripId: string,
  bookingId: string,
  updates: BookingUpdate,
  opts?: { bypassOffline?: boolean }
): Promise<void> => {
  try {
    if (!opts?.bypassOffline && networkMonitor.shouldUseOfflineDataFallback()) {
      await syncQueue.queueAction({
        type: 'update_booking',
        payload: {
          tripId,
          bookingId,
          updatesJson: JSON.stringify(updates, (_k, v) => (v instanceof Date ? v.toISOString() : v)),
        },
        tripId,
      });
      return;
    }

    const bookingRef = doc(db, 'trips', tripId, 'bookings', bookingId);

    const updateData: Record<string, any> = {
      updatedAt: serverTimestamp(),
    };
    
    if (updates.amount !== undefined) {
      updateData.amount = updates.amount;
    }
    if (updates.currency !== undefined) {
      updateData.currency = updates.currency;
    }
    if (updates.provider !== undefined) {
      updateData.provider = updates.provider;
    }
    if (updates.location !== undefined) {
      updateData.location = updates.location;
    }
    if (updates.rawEmailSubject !== undefined) {
      updateData.rawEmailSubject = updates.rawEmailSubject;
    }
    if (updates.confirmationNumber !== undefined) {
      updateData.confirmationNumber = updates.confirmationNumber;
    }
    if (updates.type !== undefined) {
      updateData.type = updates.type;
    }
    if (updates.status !== undefined) {
      updateData.status = updates.status;
    }
    if (updates.checkInDate !== undefined) {
      updateData.checkInDate = Timestamp.fromDate(updates.checkInDate);
    }
    if (updates.checkOutDate !== undefined) {
      updateData.checkOutDate = Timestamp.fromDate(updates.checkOutDate);
    }
    if (updates.departureDate !== undefined) {
      updateData.departureDate = Timestamp.fromDate(updates.departureDate);
    }
    if (updates.arrivalDate !== undefined) {
      updateData.arrivalDate = Timestamp.fromDate(updates.arrivalDate);
    }
    if (updates.airlineCode !== undefined) {
      updateData.airlineCode = updates.airlineCode;
    }
    if (updates.flightNumber !== undefined) {
      updateData.flightNumber = updates.flightNumber;
    }
    if (updates.gate !== undefined) {
      updateData.gate = updates.gate;
    }
    if (updates.terminal !== undefined) {
      updateData.terminal = updates.terminal;
    }
    if (updates.checkInLink !== undefined) {
      updateData.checkInLink = updates.checkInLink;
    }
    if (updates.checkInAvailable !== undefined) {
      updateData.checkInAvailable = updates.checkInAvailable;
    }
    if (updates.flightStatus !== undefined) {
      if (updates.flightStatus === null) {
        updateData.flightStatus = null;
      } else {
        updateData.flightStatus = {
          status: updates.flightStatus.status,
          scheduledDeparture: updates.flightStatus.scheduledDeparture
            ? Timestamp.fromDate(updates.flightStatus.scheduledDeparture)
            : null,
          actualDeparture: updates.flightStatus.actualDeparture
            ? Timestamp.fromDate(updates.flightStatus.actualDeparture)
            : null,
          scheduledArrival: updates.flightStatus.scheduledArrival
            ? Timestamp.fromDate(updates.flightStatus.scheduledArrival)
            : null,
          actualArrival: updates.flightStatus.actualArrival
            ? Timestamp.fromDate(updates.flightStatus.actualArrival)
            : null,
          delayMinutes: updates.flightStatus.delayMinutes,
          gate: updates.flightStatus.gate,
          terminal: updates.flightStatus.terminal,
          provider: updates.flightStatus.provider,
          isStale: updates.flightStatus.isStale,
          nextRefreshAt: updates.flightStatus.nextRefreshAt
            ? Timestamp.fromDate(updates.flightStatus.nextRefreshAt)
            : null,
          lastUpdated: updates.flightStatus.lastUpdated
            ? Timestamp.fromDate(updates.flightStatus.lastUpdated)
            : null,
          statusUnavailableReason: updates.flightStatus.statusUnavailableReason,
        };
      }
    }
    if (updates.lastStatusCheck !== undefined) {
      updateData.lastStatusCheck = updates.lastStatusCheck
        ? Timestamp.fromDate(updates.lastStatusCheck)
        : null;
    }
    if (updates.notificationPreferences !== undefined) {
      updateData.notificationPreferences = updates.notificationPreferences;
    }
    if (updates.split !== undefined) {
      if (updates.split === null) {
        // Remove split by setting to null (will be handled by deleteField in Firestore)
        updateData.split = null;
      } else {
        updateData.split = updates.split;
      }
    }
    
    await updateDoc(bookingRef, updateData);
    console.log('[Bookings] Updated booking');
    
    if (updates.amount !== undefined) {
      void updateTripBudgetFromBookings(tripId);
    }
  } catch (error) {
    console.error('Error updating booking:', error);
    throw error;
  }
};

/** Queue replay: revive date fields from JSON strings. */
export function reviveBookingUpdateFromQueue(raw: Record<string, unknown>): BookingUpdate {
  const u: BookingUpdate = { ...raw } as BookingUpdate;
  const asDate = (v: unknown) =>
    typeof v === 'string' ? new Date(v) : v instanceof Date ? v : undefined;
  if (raw.checkInDate !== undefined) u.checkInDate = asDate(raw.checkInDate);
  if (raw.checkOutDate !== undefined) u.checkOutDate = asDate(raw.checkOutDate);
  if (raw.departureDate !== undefined) u.departureDate = asDate(raw.departureDate);
  if (raw.arrivalDate !== undefined) u.arrivalDate = asDate(raw.arrivalDate);
  return u;
}

const reviveIsoDateFields = (obj: Record<string, unknown>, keys: string[]): void => {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === 'string') {
      (obj as Record<string, unknown>)[k] = new Date(v);
    }
  }
};

/**
 * `create_booking` queue payload: JSON.parse leaves nested dates as ISO strings.
 */
export function reviveManualBookingFromQueueJson(json: string): ManualBookingInput {
  const b = JSON.parse(json) as Record<string, unknown>;
  reviveIsoDateFields(b, ['checkInDate', 'checkOutDate', 'departureDate', 'arrivalDate', 'lastStatusCheck']);

  const fs = b.flightStatus;
  if (fs && typeof fs === 'object' && !Array.isArray(fs)) {
    reviveIsoDateFields(fs as Record<string, unknown>, [
      'scheduledDeparture',
      'actualDeparture',
      'scheduledArrival',
      'actualArrival',
      'lastUpdated',
      'nextRefreshAt',
    ]);
  }

  if (Array.isArray(b.segments)) {
    for (const seg of b.segments) {
      if (seg && typeof seg === 'object' && !Array.isArray(seg)) {
        reviveIsoDateFields(seg as Record<string, unknown>, ['departureDate', 'arrivalDate']);
      }
    }
  }

  if (Array.isArray(b.attachments)) {
    for (const att of b.attachments) {
      if (att && typeof att === 'object' && !Array.isArray(att)) {
        reviveIsoDateFields(att as Record<string, unknown>, ['createdAt', 'expiresAt']);
      }
    }
  }

  return b as ManualBookingInput;
}

export async function deleteTripBooking(tripId: string, bookingId: string): Promise<void> {
  const bookingRef = doc(db, 'trips', tripId, 'bookings', bookingId);
  await deleteDoc(bookingRef);
}

/**
 * Create itinerary items immediately for bookings with clear dates
 * This provides instant feedback to users when creating trips from email
 */
async function createItineraryItemsImmediately(
  tripId: string,
  userId: string,
  bookingIds: string[]
): Promise<{ created: number; skipped: number }> {
  let created = 0;
  let skipped = 0;

  try {
    const { createItineraryItem } = await import('./itinerary');
    const { getDoc, doc, updateDoc, serverTimestamp } = await import('firebase/firestore');
    const { db } = await import('@/lib/firebase');
    
    // Fetch all bookings by ID
    const bookingPromises = bookingIds.map(id => 
      getDoc(doc(db, 'trips', tripId, 'bookings', id))
    );
    const bookingDocs = await Promise.all(bookingPromises);
    
    for (const bookingDoc of bookingDocs) {
      if (!bookingDoc.exists()) continue;
      
      const booking = bookingDoc.data();
      const bookingId = bookingDoc.id;
      
      // Check if booking has clear dates
      const hasClearDates = !!(booking.departureDate || booking.checkInDate || booking.arrivalDate || booking.checkOutDate);
      
      if (!hasClearDates) {
        skipped++;
        continue; // Skip bookings without dates - let Firestore trigger handle them
      }
      
      try {
        // Build itinerary items based on booking type
        const items: Array<Omit<import('./itinerary').ItineraryItem, 'id' | 'tripId' | 'createdAt' | 'updatedAt'>> = [];
        
        if (booking.type === 'flight') {
          if (booking.departureDate) {
            const departureDate = booking.departureDate.toDate ? booking.departureDate.toDate() : new Date(booking.departureDate);
            items.push({
              title: `${booking.provider || 'Flight'} - Departure`,
              category: 'flight',
              startDate: departureDate,
              startTime: formatTime(departureDate),
              description: booking.confirmationNumber ? `Confirmation: ${booking.confirmationNumber}` : undefined,
              address: booking.address || booking.location || undefined,
              location: booking.location || undefined,
              bookingId,
              bookingSnapshot: {
                type: booking.type,
                provider: booking.provider,
                status: booking.status || 'confirmed',
                confirmationNumber: booking.confirmationNumber || null,
                flightNumber: booking.flightNumber || null,
                airlineCode: booking.airlineCode || null,
                gate: booking.gate || null,
                terminal: booking.terminal || null,
                checkInLink: booking.checkInLink || null,
                amount: booking.amount || null,
                currency: booking.currency || null,
                location: booking.location || null,
                startDate: departureDate,
              },
              source: 'email',
              status: booking.status === 'cancelled' ? 'cancelled' : 'planned',
              createdBy: userId,
              documents: [],
            });
          }
          
          if (booking.arrivalDate) {
            const arrivalDate = booking.arrivalDate.toDate ? booking.arrivalDate.toDate() : new Date(booking.arrivalDate);
            const departureDate = booking.departureDate ? (booking.departureDate.toDate ? booking.departureDate.toDate() : new Date(booking.departureDate)) : null;
            
            // Only create arrival item if it's different from departure
            if (!departureDate || startOfDay(arrivalDate).getTime() !== startOfDay(departureDate).getTime()) {
              items.push({
                title: `${booking.provider || 'Flight'} - Arrival`,
                category: 'flight',
                startDate: arrivalDate,
                startTime: formatTime(arrivalDate),
                description: booking.confirmationNumber ? `Confirmation: ${booking.confirmationNumber}` : undefined,
                address: booking.address || booking.location || undefined,
                location: booking.location || undefined,
                bookingId,
                bookingSnapshot: {
                  type: booking.type,
                  provider: booking.provider,
                  status: booking.status || 'confirmed',
                  confirmationNumber: booking.confirmationNumber || null,
                  flightNumber: booking.flightNumber || null,
                  airlineCode: booking.airlineCode || null,
                  gate: booking.gate || null,
                  terminal: booking.terminal || null,
                  checkInLink: booking.checkInLink || null,
                  amount: booking.amount || null,
                  currency: booking.currency || null,
                  location: booking.location || null,
                  startDate: arrivalDate,
                },
                source: 'email',
                status: booking.status === 'cancelled' ? 'cancelled' : 'planned',
                createdBy: userId,
                documents: [],
              });
            }
          }
        } else if (booking.type === 'hotel') {
          if (booking.checkInDate) {
            const checkInDate = booking.checkInDate.toDate ? booking.checkInDate.toDate() : new Date(booking.checkInDate);
            items.push({
              title: `${booking.provider || 'Hotel'} - Check-in`,
              category: 'hotel',
              startDate: checkInDate,
              startTime: '15:00', // Default check-in time
              description: booking.confirmationNumber ? `Confirmation: ${booking.confirmationNumber}` : undefined,
              address: booking.address || booking.location || undefined,
              location: booking.location || undefined,
              bookingId,
              bookingSnapshot: {
                type: booking.type,
                provider: booking.provider,
                status: booking.status || 'confirmed',
                confirmationNumber: booking.confirmationNumber || null,
                amount: booking.amount || null,
                currency: booking.currency || null,
                location: booking.location || null,
                startDate: checkInDate,
              },
              source: 'email',
              status: booking.status === 'cancelled' ? 'cancelled' : 'planned',
              createdBy: userId,
              documents: [],
            });
          }
          
          if (booking.checkOutDate) {
            const checkOutDate = booking.checkOutDate.toDate ? booking.checkOutDate.toDate() : new Date(booking.checkOutDate);
            const checkInDate = booking.checkInDate ? (booking.checkInDate.toDate ? booking.checkInDate.toDate() : new Date(booking.checkInDate)) : null;
            
            // Only create check-out item if it's different from check-in
            if (!checkInDate || startOfDay(checkOutDate).getTime() !== startOfDay(checkInDate).getTime()) {
              items.push({
                title: `${booking.provider || 'Hotel'} - Check-out`,
                category: 'hotel',
                startDate: checkOutDate,
                startTime: '11:00', // Default check-out time
                description: booking.confirmationNumber ? `Confirmation: ${booking.confirmationNumber}` : undefined,
                address: booking.address || booking.location || undefined,
                location: booking.location || undefined,
                bookingId,
                bookingSnapshot: {
                  type: booking.type,
                  provider: booking.provider,
                  status: booking.status || 'confirmed',
                  confirmationNumber: booking.confirmationNumber || null,
                  amount: booking.amount || null,
                  currency: booking.currency || null,
                  location: booking.location || null,
                  startDate: checkOutDate,
                },
                source: 'email',
                status: booking.status === 'cancelled' ? 'cancelled' : 'planned',
                createdBy: userId,
                documents: [],
              });
            }
          }
        } else if (booking.type === 'activity') {
          const activityDate = booking.departureDate || booking.checkInDate || booking.arrivalDate;
          if (activityDate) {
            const date = activityDate.toDate ? activityDate.toDate() : new Date(activityDate);
            items.push({
              title: `${booking.provider || 'Activity'}`,
              category: 'activity',
              startDate: date,
              startTime: formatTime(date),
              description: booking.confirmationNumber ? `Confirmation: ${booking.confirmationNumber}` : undefined,
              address: booking.address || booking.location || undefined,
              location: booking.location || undefined,
              bookingId,
              bookingSnapshot: {
                type: booking.type,
                provider: booking.provider,
                status: booking.status || 'confirmed',
                confirmationNumber: booking.confirmationNumber || null,
                amount: booking.amount || null,
                currency: booking.currency || null,
                location: booking.location || null,
                startDate: date,
              },
              source: 'email',
              status: booking.status === 'cancelled' ? 'cancelled' : 'planned',
              createdBy: userId,
              documents: [],
            });
          }
        } else if (booking.type === 'ferry' || booking.type === 'transportation') {
          const transportDate = booking.departureDate || booking.checkInDate;
          if (transportDate) {
            const date = transportDate.toDate ? transportDate.toDate() : new Date(transportDate);
            const isFerry = booking.type === 'ferry';
            items.push({
              title: isFerry ? `${booking.provider || 'Ferry'}` : `${booking.provider || 'Transport'}`,
              category: isFerry ? 'ferry' : 'transportation',
              startDate: date,
              startTime: booking.startTime || formatTime(date),
              description: booking.confirmationNumber ? `Confirmation: ${booking.confirmationNumber}` : undefined,
              address: booking.address || booking.location || undefined,
              location: booking.location || undefined,
              bookingId,
              bookingSnapshot: {
                type: booking.type,
                provider: booking.provider,
                status: booking.status || 'confirmed',
                confirmationNumber: booking.confirmationNumber || null,
                amount: booking.amount || null,
                currency: booking.currency || null,
                location: booking.location || null,
                startDate: date,
              },
              source: 'email',
              status: booking.status === 'cancelled' ? 'cancelled' : 'planned',
              createdBy: userId,
              documents: [],
            });
          }
        } else {
          // Other types - use any available date
          const fallbackDate = booking.departureDate || booking.checkInDate || booking.arrivalDate || booking.checkOutDate;
          if (fallbackDate) {
            const date = fallbackDate.toDate ? fallbackDate.toDate() : new Date(fallbackDate);
            items.push({
              title: `${booking.provider || booking.type || 'Booking'}`,
              category: 'other',
              startDate: date,
              startTime: formatTime(date),
              description: booking.confirmationNumber ? `Confirmation: ${booking.confirmationNumber}` : undefined,
              address: booking.address || booking.location || undefined,
              location: booking.location || undefined,
              bookingId,
              bookingSnapshot: {
                type: booking.type,
                provider: booking.provider,
                status: booking.status || 'confirmed',
                confirmationNumber: booking.confirmationNumber || null,
                amount: booking.amount || null,
                currency: booking.currency || null,
                location: booking.location || null,
                startDate: date,
              },
              source: 'email',
              status: booking.status === 'cancelled' ? 'cancelled' : 'planned',
              createdBy: userId,
              documents: [],
            });
          }
        }
        
        // Create all itinerary items for this booking
        const itemIds: string[] = [];
        for (const item of items) {
          try {
            const itemId = await createItineraryItem(tripId, item);
            itemIds.push(itemId);
            created++;
          } catch (itemError) {
            console.warn(`[createItineraryItemsImmediately] Failed to create itinerary item for booking ${bookingId}:`, itemError);
          }
        }
        
        // Update booking with itinerary mapping status
        if (itemIds.length > 0) {
          await updateDoc(doc(db, 'trips', tripId, 'bookings', bookingId), {
            itineraryMappingStatus: 'mapped',
            itineraryItemIds: itemIds,
            itineraryMappingUpdatedAt: serverTimestamp(),
          });
        } else {
          // No items created - mark as pending for Firestore trigger
          await updateDoc(doc(db, 'trips', tripId, 'bookings', bookingId), {
            itineraryMappingStatus: 'pending',
          });
          skipped++;
        }
      } catch (bookingError) {
        console.warn(`[createItineraryItemsImmediately] Error processing booking ${bookingId}:`, bookingError);
        skipped++;
      }
    }
  } catch (error) {
    console.error('[createItineraryItemsImmediately] Error:', error);
    // Don't throw - let Firestore trigger handle any missed bookings
  }
  
  return { created, skipped };
}

// Helper functions
function startOfDay(date: Date): Date {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
}

function formatTime(date: Date): string {
  return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
}

const normalizeDetectedAttachments = (
  attachments: Array<{
    id?: string;
    url?: string;
    name?: string;
    filename?: string;
    contentType?: string;
    mimeType?: string;
    size?: number;
    source?: string;
  }> | undefined,
): ItineraryAttachment[] | undefined => {
  if (!Array.isArray(attachments) || attachments.length === 0) {
    return undefined;
  }

  const normalized = attachments
    .filter((att) => typeof att?.url === 'string' && att.url.trim().length > 0)
    .map((att, index) => ({
      id: (att.id && att.id.trim()) || `email-att-${index + 1}`,
      name: (att.name || att.filename || `Attachment ${index + 1}`).trim(),
      url: att.url!.trim(),
      contentType: att.contentType || att.mimeType || null,
      size: typeof att.size === 'number' ? att.size : null,
      source: coerceAttachmentSource(att.source) ?? 'email',
    }));

  return normalized.length > 0 ? normalized : undefined;
};

/**
 * Store bookings from a detected trip
 * This stores bookings directly from DetectedTrip.bookings when a trip is created from detection
 */
export const storeDetectedBookings = async (
  tripId: string,
  userId: string,
  bookings: Array<Omit<ExtractedBooking, 'checkInDate' | 'checkOutDate' | 'departureDate' | 'arrivalDate' | 'rawEmailDate'> & {
    checkInDate?: string;
    checkOutDate?: string;
    departureDate?: string;
    arrivalDate?: string;
    rawEmailDate?: string;
  }>,
  provider: InboxProviderId,
  options?: {
    autoExpandTripDates?: boolean;
    createItineraryItems?: boolean;
  },
): Promise<void> => {
  if (!bookings.length) return;
  const autoExpandTripDates = options?.autoExpandTripDates ?? true;
  // Keep server trigger (`functions/src/itinerarySync.ts`) as the authoritative mapper.
  const createItineraryItems = options?.createItineraryItems ?? false;

  try {
    const batch = writeBatch(db);
    const bookingsRef = collection(db, 'trips', tripId, 'bookings');
    const bookingIds: string[] = []; // Track booking IDs for itinerary creation

    const byDeterministicId = new Map<string, (typeof bookings)[number]>();
    for (const booking of bookings) {
      const id = emailBookingDeterministicDocId(booking as unknown as Record<string, unknown>);
      byDeterministicId.set(id, booking);
    }
    const consolidated = Array.from(byDeterministicId.entries());

    const existingIds = new Set<string>();
    for (let i = 0; i < consolidated.length; i += 30) {
      const chunk = consolidated.slice(i, i + 30).map(([docId]) => docId);
      if (chunk.length === 0) continue;
      const q = query(bookingsRef, where(documentId(), 'in', chunk));
      const snap = await getDocs(q);
      snap.forEach((d) => existingIds.add(d.id));
    }

    consolidated.forEach(([bookingDocId, booking]) => {
      const bookingRef = doc(bookingsRef, bookingDocId);
      
      // Ensure type is set - default to 'other' if missing, but try to infer from subject if possible
      let bookingType = booking.type || 'other';
      if (bookingType === 'other' && booking.rawEmailSubject) {
        // Try to infer type from subject as fallback
        const subjectLower = booking.rawEmailSubject.toLowerCase();
        if (subjectLower.includes('hotel') || subjectLower.includes('reservation') || subjectLower.includes('check-in')) {
          bookingType = 'hotel';
        } else if (subjectLower.includes('activity') || subjectLower.includes('ticket') || subjectLower.includes('tour') || 
                   subjectLower.includes('disney') || subjectLower.includes('observation') || subjectLower.includes('sky')) {
          bookingType = 'activity';
        } else if (subjectLower.includes('restaurant') || subjectLower.includes('dining') || subjectLower.includes('table')) {
          bookingType = 'restaurant';
        } else if (subjectLower.includes('car') || subjectLower.includes('rental') || subjectLower.includes('vehicle')) {
          bookingType = 'car';
        } else if (subjectLower.includes('flight') || subjectLower.includes('airline') || subjectLower.includes('departure')) {
          bookingType = 'flight';
        }
      }
      
      const firestoreBooking: Record<string, any> = {
        type: bookingType, // Use inferred type
        provider: booking.provider,
        tripId,
        userId,
        createdAt: serverTimestamp(),
        syncedAt: serverTimestamp(),
        source: 'email',
        emailProvider: provider,
        status: 'confirmed', // Default to confirmed for detected bookings
        itineraryMappingStatus: 'pending', // Trigger Firestore function to create itinerary items
        itineraryItemIds: [], // Will be populated by itinerary sync
      };

      if (booking.confirmationNumber) {
        firestoreBooking.confirmationNumber = booking.confirmationNumber;
      }
      if (booking.sourceAttachment) {
        firestoreBooking.sourceAttachment = {
          filename: booking.sourceAttachment.filename,
          mimeType: booking.sourceAttachment.mimeType,
        };
      }
      const normalizedAttachments = normalizeDetectedAttachments(
        (booking.attachments as Array<{
          id?: string;
          url?: string;
          name?: string;
          filename?: string;
          contentType?: string;
          mimeType?: string;
          size?: number;
          source?: string;
        }> | undefined),
      );
      if (normalizedAttachments) {
        firestoreBooking.attachments = normalizedAttachments;
      }
      // Helper function to safely convert values into Date objects (Firestore will store as Timestamp)
      const safeDate = (dateValue: string | Date | undefined, fieldName: string): Date | null => {
        if (!dateValue) return null;
        try {
          const date = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
          if (!date || Number.isNaN(date.getTime())) {
            console.warn(`[storeDetectedBookings] Invalid ${fieldName} date:`, dateValue);
            return null;
          }
          return date;
        } catch (error) {
          console.error(`[storeDetectedBookings] Error converting ${fieldName}:`, error, dateValue);
          return null;
        }
      };

      if (booking.checkInDate) {
        const dateValue = safeDate(booking.checkInDate, 'checkInDate');
        if (dateValue) firestoreBooking.checkInDate = dateValue;
      }
      if (booking.checkOutDate) {
        const dateValue = safeDate(booking.checkOutDate, 'checkOutDate');
        if (dateValue) firestoreBooking.checkOutDate = dateValue;
      }
      if (booking.departureDate) {
        const dateValue = safeDate(booking.departureDate, 'departureDate');
        if (dateValue) firestoreBooking.departureDate = dateValue;
      }
      if (booking.arrivalDate) {
        const dateValue = safeDate(booking.arrivalDate, 'arrivalDate');
        if (dateValue) firestoreBooking.arrivalDate = dateValue;
      }
      if (booking.location) {
        firestoreBooking.location = booking.location;
      }
      if ((booking as any).origin) {
        firestoreBooking.origin = (booking as any).origin;
      }
      if ((booking as any).destination) {
        firestoreBooking.destination = (booking as any).destination;
      }
      if ((booking as any).flightNumber) {
        firestoreBooking.flightNumber = (booking as any).flightNumber;
      }
      if ((booking as any).airlineCode) {
        firestoreBooking.airlineCode = (booking as any).airlineCode;
      }
      if (Array.isArray((booking as any).segments) && (booking as any).segments.length > 0) {
        // Store as-is (dates may be strings from Functions responses); itinerary sync can parse strings/timestamps.
        firestoreBooking.segments = (booking as any).segments;
      }
      if (booking.amount !== undefined) {
        firestoreBooking.amount = booking.amount;
      }
      if (booking.currency) {
        firestoreBooking.currency = booking.currency;
      }
      if (booking.rawEmailSubject) {
        firestoreBooking.rawEmailSubject = booking.rawEmailSubject;
      }
      if (booking.rawEmailDate) {
        const dateValue = safeDate(booking.rawEmailDate, 'rawEmailDate');
        if (dateValue) firestoreBooking.rawEmailDate = dateValue;
      }
      if (booking.confidence !== undefined) {
        firestoreBooking.confidence = booking.confidence;
      }
      if (booking.labels && booking.labels.length > 0) {
        firestoreBooking.labels = booking.labels;
      }

      const isUpdate = existingIds.has(bookingDocId);
      if (isUpdate) {
        delete firestoreBooking.itineraryItemIds;
        delete firestoreBooking.itineraryMappingStatus;
        delete firestoreBooking.createdAt;
      }

      batch.set(bookingRef, firestoreBooking, { merge: true });
      bookingIds.push(bookingDocId);
    });

    await batch.commit();
    console.log(`[storeDetectedBookings] Stored ${consolidated.length} booking doc(s) from provider ${provider}`);
    
    if (autoExpandTripDates) {
      // Ensure trip date range covers these bookings so itinerary creation won't fail
      try {
        const tripRef = doc(db, 'trips', tripId);
        const tripDoc = await getDoc(tripRef);
        if (tripDoc.exists()) {
          const tripData = tripDoc.data();
          let tripStart: Date | null = tripData?.startDate?.toDate ? tripData.startDate.toDate() : tripData?.startDate ? new Date(tripData.startDate) : null;
          let tripEnd: Date | null = tripData?.endDate?.toDate ? tripData.endDate.toDate() : tripData?.endDate ? new Date(tripData.endDate) : null;

          const allDates: Date[] = [];
          const pushDate = (d?: Date | null) => {
            if (d && !Number.isNaN(d.getTime())) allDates.push(d);
          };
          consolidated.map(([, b]) => b).forEach((b) => {
            pushDate(typeof b.departureDate === 'string' ? new Date(b.departureDate) : (b.departureDate as any));
            pushDate(typeof b.arrivalDate === 'string' ? new Date(b.arrivalDate) : (b.arrivalDate as any));
            pushDate(typeof b.checkInDate === 'string' ? new Date(b.checkInDate) : (b.checkInDate as any));
            pushDate(typeof b.checkOutDate === 'string' ? new Date(b.checkOutDate) : (b.checkOutDate as any));
          });
          if (allDates.length > 0) {
            allDates.sort((a, b) => a.getTime() - b.getTime());
            const newStart = tripStart ? new Date(Math.min(tripStart.getTime(), allDates[0].getTime())) : allDates[0];
            const newEnd = tripEnd ? new Date(Math.max(tripEnd.getTime(), allDates[allDates.length - 1].getTime())) : allDates[allDates.length - 1];
            if (!tripStart || !tripEnd || newStart.getTime() !== tripStart.getTime() || newEnd.getTime() !== tripEnd.getTime()) {
              await updateDoc(tripRef, {
                startDate: Timestamp.fromDate(newStart),
                endDate: Timestamp.fromDate(newEnd),
                updatedAt: serverTimestamp(),
              });
              tripStart = newStart;
              tripEnd = newEnd;
              console.log('[storeDetectedBookings] Auto-expanded trip date window');
            }
          }
        }
      } catch (expandError) {
        const message = expandError instanceof Error ? expandError.message : String(expandError);
        console.warn('[storeDetectedBookings] Failed to auto-expand trip dates', { message });
      }
    }

    if (createItineraryItems) {
      // Immediately create itinerary items for bookings with clear dates
      // This is opt-in only; default path is server-side itinerary sync for consistency.
      try {
        const result = await createItineraryItemsImmediately(tripId, userId, bookingIds);
        console.log(`[storeDetectedBookings] Created ${result.created} itinerary items immediately, ${result.skipped} will be handled by trigger`);
      } catch (itineraryError) {
        console.warn(`[storeDetectedBookings] Failed to create itinerary items immediately:`, itineraryError);
        // Continue - Firestore trigger will handle it
      }
    }
    
    // Calculate and auto-set trip budget from booking amounts if no budget exists
    await updateTripBudgetFromBookings(tripId);
  } catch (error) {
    console.error('[storeDetectedBookings] Error storing bookings:', error);
    throw error;
  }
};

/**
 * Calculate total budget from bookings and update trip budget based on budgetUpdateMode
 */
async function updateTripBudgetFromBookings(tripId: string): Promise<void> {
  try {
    // Get trip to check current budget, base currency, and update mode
    const tripRef = doc(db, 'trips', tripId);
    const tripDoc = await getDoc(tripRef);
    
    if (!tripDoc.exists()) {
      console.warn('[updateTripBudgetFromBookings] Trip not found');
      return;
    }
    
    const tripData = tripDoc.data();
    const currentBudget = tripData?.budget;
    const baseCurrency = tripData?.baseCurrency || 'USD';
    const budgetUpdateMode: 'auto-set' | 'auto-add' | 'manual' = tripData?.budgetUpdateMode || 'auto-set';
    
    // If mode is 'manual', don't auto-update
    if (budgetUpdateMode === 'manual') {
      console.log('[updateTripBudgetFromBookings] Manual budget mode, skipping auto-update');
      return;
    }
    
    // For 'auto-set': only set if no budget exists
    if (budgetUpdateMode === 'auto-set') {
      if (currentBudget !== null && currentBudget !== undefined) {
        console.log('[updateTripBudgetFromBookings] Existing budget found, skipping auto-set');
        return;
      }
    }
    // For 'auto-add': always add booking amounts to existing budget (or set if none exists)
    
    // Get all bookings for this trip
    const bookingsRef = collection(db, 'trips', tripId, 'bookings');
    const bookingsSnapshot = await getDocs(bookingsRef);
    
    const bookingAmounts: Array<{ amount: number; currency: string }> = [];
    bookingsSnapshot.forEach((bookingDoc) => {
      const bookingData = bookingDoc.data();
      if (bookingData.amount && bookingData.currency) {
        bookingAmounts.push({
          amount: bookingData.amount,
          currency: bookingData.currency,
        });
      }
    });
    
    if (bookingAmounts.length === 0) {
      console.log('[updateTripBudgetFromBookings] No booking amounts found');
      return;
    }
    
    // Convert all amounts to base currency and sum
    const { convertToBaseCurrency } = await import('@/utils/currency');
    const bookingTotal = await convertToBaseCurrency(bookingAmounts, baseCurrency);
    
    // Calculate final budget based on mode
    let finalBudget: number;
    if (budgetUpdateMode === 'auto-add') {
      // Add booking amounts to existing budget (or set if none exists)
      finalBudget = (currentBudget || 0) + bookingTotal;
      console.log(`[updateTripBudgetFromBookings] Auto-add: ${baseCurrency} ${bookingTotal.toFixed(2)} to existing budget ${currentBudget || 0} = ${finalBudget.toFixed(2)}`);
    } else {
      // auto-set: use booking total as budget
      finalBudget = bookingTotal;
      console.log('[updateTripBudgetFromBookings] Auto-set budget from detected bookings');
    }
    
    // Update trip budget
    await updateDoc(tripRef, {
      budget: finalBudget,
      updatedAt: serverTimestamp(),
    });
    
    console.log('[updateTripBudgetFromBookings] Updated trip budget', { mode: budgetUpdateMode });
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    console.error('[updateTripBudgetFromBookings] Error updating budget', { message });
    // Don't throw - budget update is not critical
  }
}

